// How a polish finding was established. Each detector attaches the grounds it actually produced; the pass
// root declares where the evidence lives. proofOf derives everything else, and the two rules below are the
// only place it decides how later LLM stages may treat the finding.
import { z } from 'zod'

// SECRET_PATTERNS labels a client may legitimately hold: Google's publishable web key, and a JWT that may
// be the client's own session token. Every other credential label proves a leak whatever its literal value.
export const GOOGLE_API_KEY_LABEL = 'Google API key'
export const CLIENT_SAFE_CREDENTIAL_LABELS: ReadonlySet<string> = new Set([GOOGLE_API_KEY_LABEL, 'JSON Web Token'])

const EVIDENCE_GROUNDS = [
  // An upstream judge already ruled it a real defect and routed it to this pass.
  z.object({ kind: z.literal('adjudicated') }),
  // An LLM oracle decided the finding exists.
  z.object({ kind: z.literal('llm-verdict') }),
  // Credential-format matches, by SECRET_PATTERNS label.
  z.object({ kind: z.literal('credential-match'), labels: z.array(z.string()).min(1) }),
  // App content (field names or values, identifiers, copy, source) read through a vocabulary.
  z.object({ kind: z.literal('vocabulary-match') }),
  // A marker the active probe itself injected, observed coming back.
  z.object({ kind: z.literal('injected-marker'), marker: z.string().min(1) }),
  // A live or recorded measurement. Whether it is the defect by definition depends on the signal's
  // do-not-file exceptions, so it stays declared until those are typed.
  z.object({ kind: z.literal('measurement'), declared: z.enum(['measured', 'pattern']) }),
  // No grounds were recorded or they could not be read.
  z.object({ kind: z.literal('unknown') }),
] as const

const EvidenceGroundsSchema = z.discriminatedUnion('kind', [...EVIDENCE_GROUNDS])
export type EvidenceGrounds = z.infer<typeof EvidenceGroundsSchema>

export const FindingGroundsSchema = z.discriminatedUnion('kind', [
  ...EVIDENCE_GROUNDS,
  // The finding keeps the pre-#7119 `mustFile` treatment (filing and triage protected) until the tracked
  // change can derive it; `evidence` is what the detector actually produced.
  z.object({
    kind: z.literal('transitional'),
    evidence: EvidenceGroundsSchema,
    tracking: z.enum(['#7117', 'typed-signal-exceptions', 'legacy-seed']),
  }),
])

export type FindingGrounds = z.infer<typeof FindingGroundsSchema>
type TransitionalTracking = Extract<FindingGrounds, { kind: 'transitional' }>['tracking']

export const VOCABULARY_MATCH: EvidenceGrounds = { kind: 'vocabulary-match' }
export const LLM_VERDICT: EvidenceGrounds = { kind: 'llm-verdict' }
export const UNKNOWN_GROUNDS: EvidenceGrounds = { kind: 'unknown' }

export function declared(basis: 'measured' | 'pattern'): EvidenceGrounds {
  return { kind: 'measurement', declared: basis }
}

export function transitional(evidence: EvidenceGrounds, tracking: TransitionalTracking): FindingGrounds {
  return { kind: 'transitional', evidence, tracking }
}

export type EvidenceBasis = 'adjudicated' | 'llm-verdict' | 'credential-format' | 'self-report' | 'measured' | 'pattern' | 'unknown'

// Where the evidence lives: the recording the filing investigator reads, or the live app the active probe hit.
export type EvidenceLocus = 'recording' | 'live-probe'

export interface FindingProof {
  grounds: FindingGrounds
  basis: EvidenceBasis
  locus: EvidenceLocus
}

export function basisOf(grounds: FindingGrounds): EvidenceBasis {
  switch (grounds.kind) {
    case 'adjudicated': return 'adjudicated'
    case 'llm-verdict': return 'llm-verdict'
    case 'credential-match': return grounds.labels.some((l) => !CLIENT_SAFE_CREDENTIAL_LABELS.has(l)) ? 'credential-format' : 'pattern'
    case 'vocabulary-match': return 'self-report'
    case 'injected-marker': return 'measured'
    case 'measurement': return grounds.declared
    case 'unknown': return 'unknown'
    case 'transitional': return basisOf(grounds.evidence)
  }
}

export function proofOf(grounds: FindingGrounds, locus: EvidenceLocus): FindingProof {
  return { grounds, basis: basisOf(grounds), locus }
}

// Whether the basis alone already establishes the defect.
function established(basis: EvidenceBasis): boolean {
  switch (basis) {
    case 'adjudicated':
    case 'credential-format':
    case 'measured':
      return true
    case 'llm-verdict':
    case 'self-report':
    case 'pattern':
    case 'unknown':
      return false
  }
}

function settled(grounds: FindingGrounds): boolean {
  return grounds.kind === 'transitional' || established(basisOf(grounds))
}

// Whether filing may conclude "not a defect" (the investigator's skip_bug): only an unestablished finding
// whose evidence the investigator can re-read in the recording.
export function filingMayReject(proof: FindingProof): boolean {
  return !settled(proof.grounds) && proof.locus === 'recording'
}

// Whether a pre-filing triage judge may drop the finding.
export function triageMayDrop(grounds: FindingGrounds): boolean {
  return !settled(grounds)
}
